import {
  MoreVertical,
  ArrowUp,
  ArrowDown,
  Pencil,
  Trash2,
  Square,
  SquareX,
  SquareSlash,
  FlipVertical2,
  FolderMinus,
  Move3D,
  Waves,
  Spline,
  PenTool,
  Scissors,
  Merge,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface LayerOverflowMenuProps {
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** True when this layer already has a mask attached. Drives
   *  Add Mask vs Delete Mask in the menu. */
  hasMask: boolean;
  /** Mask kind — 'raster' or 'vector'. Drives the Convert entry's
   *  target kind label and the Edit Vector Path entry's visibility.
   *  Tier 2 Group A PR-D. */
  maskKind: 'raster' | 'vector';
  /** True when this layer's mask is currently enabled. Reflected in
   *  the Disable / Enable menu label and the `mask.enabled` toggle. */
  maskEnabled: boolean;
  /** True when this layer's mask is currently inverted. Reflected
   *  in the Invert toggle's checked state. */
  maskInverted: boolean;
  /** True when there's a finalized marquee selection right now —
   *  Add Mask uses it to seed the mask alpha (selected = visible),
   *  matching Photoshop's "Add Vector Mask From Selection." */
  hasSelection: boolean;
  /** True when this row is a group node — surfaces the Ungroup
   *  item, hides Add Mask (groups can't carry masks themselves;
   *  mask their children instead). */
  isGroup: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRename: () => void;
  onDelete: () => void;
  onAddMask: () => void;
  /** Add a vector mask. Surfaces alongside Add Mask when the
   *  layer has no mask yet. Tier 2 Group A PR-D. */
  onAddVectorMask: () => void;
  onDeleteMask: () => void;
  /** Toggle the mask's `enabled` field — disabled masks render as
   *  fully-opaque alpha (layer unmasked) without losing the painted
   *  raw alpha. */
  onToggleMaskEnabled: () => void;
  /** Toggle the mask's `inverted` field — inverted masks render as
   *  255 - raw_alpha, swapping the hide / reveal regions. */
  onToggleMaskInverted: () => void;
  /** Convert the mask between raster and vector kinds. Tier 2
   *  Group A PR-D. The menu surfaces a single entry whose label
   *  flips based on current `maskKind`; calls the appropriate
   *  direction. EditorPage owns the trace / seed / re-rasterize
   *  flow. */
  onConvertMaskToVector: () => void;
  onConvertMaskToRaster: () => void;
  /** Enter Path Edit mode on a vector mask's path. Surfaced
   *  only when `maskKind === 'vector'`. Tier 2 Group A PR-D. */
  onEditVectorMaskPath: () => void;
  onUngroup: () => void;
  /** Free Transform — Tier 1 Group E. Opens the Transform mode
   *  on this layer. Hidden on group rows (groups don't have a
   *  single Fabric object to transform; would need a flatten-or-
   *  transform-recursively decision deferred). */
  onTransform: () => void;
  /** Liquify — Tier 1 Group E final piece. Opens the full-screen
   *  brush-based deformation workspace on this layer. Same hidden-
   *  on-group reasoning as Free Transform — Liquify needs a single
   *  rasterizable surface. */
  onLiquify: () => void;
  /** True when this row is a vector layer — surfaces the
   *  "Edit Path…" entry. Hidden on raster / group / adjustment
   *  rows (those have no path to edit). */
  isVector: boolean;
  /** Open Path Edit mode on this vector layer (Tier 2 Group A
   *  PR-B). No-op when `isVector` is false. */
  onEditPath: () => void;
  /** Crop the layer's backing pixels to the alpha bounding box of
   *  its non-transparent content. Hidden on group rows (no single
   *  rasterizable surface) and on adjustment / text / shape /
   *  vector rows (their bounds are already tight to content).
   *  EditorPage decides eligibility via `canTrim` below. */
  onTrim: () => void;
  /** True when Trim is a meaningful op for this row. False for
   *  group / adjustment / text / shape / vector layers. When
   *  false the entry is hidden entirely rather than disabled —
   *  the menu is already long and a disabled row would be noise. */
  canTrim: boolean;
  /** Merge the active layer + every additionally-selected layer
   *  into a single raster layer. Surfaced only when the row is
   *  participating in a 2+-layer selection (`canMerge`). Hidden
   *  otherwise — the menu doesn't surface "Merge Layers" on a
   *  single-layer selection since there's nothing to merge with. */
  onMerge: () => void;
  /** True when ≥2 layers (including this row) are selected and
   *  share a parent. Drives the "Merge Layers" entry's visibility.
   *  EditorPage computes this from `activeLayerId ∪ selectedLayerIds`
   *  filtered to the active layer's parent. */
  canMerge: boolean;
  /** True when this row participates in a 2+-layer same-parent
   *  selection. Equivalent to `canMerge` (same predicate) but
   *  named for its OTHER purpose: deciding which menu entries
   *  should be DISABLED because they can't sensibly fan out to a
   *  multi-target action.
   *
   *  Items disabled in multi-select mode (each routed through the
   *  per-row callback, which would otherwise misleadingly act on
   *  just this one row while the user thought the selection
   *  applied):
   *    • Rename       — one name can't span N layers.
   *    • Free Transform — multi-target transform requires Fabric
   *                       ActiveSelection plumbing that v1 doesn't
   *                       have; per-layer Transform of N layers
   *                       sequentially is bad UX. Use Merge Layers
   *                       first to collapse, then transform.
   *    • Liquify      — single-surface brush workspace.
   *    • Edit Path    — single vector layer's path; no meaning on
   *                     multiple targets.
   *    • Mask sub-ops (toggle / invert / convert / delete / edit
   *                    vector path) — per-mask state; multi-select
   *                    is opaque about which mask "wins."
   *  Items that fan out at the EditorPage handler layer (visible
   *  + enabled in multi-select): Move Up / Down, Add Mask, Add
   *  Vector Mask, Trim, Delete. */
  isMultiSelected: boolean;
}

const MULTI_SELECT_DISABLED_TITLE =
  'Disabled while multiple layers are selected — use the per-layer overflow menu after deselecting.';

/** Per-row overflow menu surfaced from the LayerRow's `⋮` icon.
 *  Tier 1 row redesign collapses the previously-inline up / down /
 *  trash buttons into this single trigger.
 *
 *  The roadmap calls for additional sections (Mask, Group,
 *  Clipping, Lock, Transform) once those features ship. Adding
 *  them later is a pure additive change to this file — no
 *  consumer-side wiring beyond the new prop callbacks. */
export function LayerOverflowMenu({
  canMoveUp,
  canMoveDown,
  hasMask,
  maskKind,
  maskEnabled,
  maskInverted,
  hasSelection,
  isGroup,
  onMoveUp,
  onMoveDown,
  onRename,
  onDelete,
  onAddMask,
  onAddVectorMask,
  onDeleteMask,
  onToggleMaskEnabled,
  onToggleMaskInverted,
  onConvertMaskToVector,
  onConvertMaskToRaster,
  onEditVectorMaskPath,
  onUngroup,
  onTransform,
  onLiquify,
  isVector,
  onEditPath,
  onTrim,
  canTrim,
  onMerge,
  canMerge,
  isMultiSelected,
}: LayerOverflowMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent shrink-0"
          aria-label="Layer options"
          title="Layer options"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="text-[10px] uppercase text-muted-foreground">
          Layer
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={onRename}
          disabled={isMultiSelected}
          title={isMultiSelected ? MULTI_SELECT_DISABLED_TITLE : undefined}
          className="text-xs"
        >
          <Pencil className="h-3.5 w-3.5 mr-2" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onMoveUp} disabled={!canMoveUp} className="text-xs">
          <ArrowUp className="h-3.5 w-3.5 mr-2" />
          Move Up
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onMoveDown} disabled={!canMoveDown} className="text-xs">
          <ArrowDown className="h-3.5 w-3.5 mr-2" />
          Move Down
        </DropdownMenuItem>
        {!isGroup && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase text-muted-foreground">
              Mask
            </DropdownMenuLabel>
            {hasMask ? (
              <>
                <DropdownMenuItem
                  onClick={onToggleMaskEnabled}
                  disabled={isMultiSelected}
                  title={isMultiSelected ? MULTI_SELECT_DISABLED_TITLE : undefined}
                  className="text-xs"
                >
                  <SquareSlash className="h-3.5 w-3.5 mr-2" />
                  {maskEnabled ? 'Disable Mask' : 'Enable Mask'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onToggleMaskInverted}
                  disabled={isMultiSelected}
                  title={isMultiSelected ? MULTI_SELECT_DISABLED_TITLE : undefined}
                  className="text-xs"
                >
                  <FlipVertical2 className="h-3.5 w-3.5 mr-2" />
                  {maskInverted ? 'Un-invert Mask' : 'Invert Mask'}
                </DropdownMenuItem>
                {maskKind === 'vector' ? (
                  <>
                    <DropdownMenuItem
                      onClick={onEditVectorMaskPath}
                      disabled={isMultiSelected}
                      title={isMultiSelected ? MULTI_SELECT_DISABLED_TITLE : undefined}
                      className="text-xs"
                    >
                      <Spline className="h-3.5 w-3.5 mr-2" />
                      Edit Vector Mask Path…
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={onConvertMaskToRaster}
                      disabled={isMultiSelected}
                      title={isMultiSelected ? MULTI_SELECT_DISABLED_TITLE : undefined}
                      className="text-xs"
                    >
                      <Square className="h-3.5 w-3.5 mr-2" />
                      Convert to Raster Mask
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem
                    onClick={onConvertMaskToVector}
                    disabled={isMultiSelected}
                    title={isMultiSelected ? MULTI_SELECT_DISABLED_TITLE : undefined}
                    className="text-xs"
                  >
                    <PenTool className="h-3.5 w-3.5 mr-2" />
                    Convert to Vector Mask
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={onDeleteMask}
                  disabled={isMultiSelected}
                  title={isMultiSelected ? MULTI_SELECT_DISABLED_TITLE : undefined}
                  className="text-xs"
                >
                  <SquareX className="h-3.5 w-3.5 mr-2" />
                  Delete Mask
                </DropdownMenuItem>
              </>
            ) : (
              <>
                <DropdownMenuItem onClick={onAddMask} className="text-xs">
                  <Square className="h-3.5 w-3.5 mr-2" />
                  {hasSelection ? 'Add Mask From Selection' : 'Add Mask'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onAddVectorMask} className="text-xs">
                  <PenTool className="h-3.5 w-3.5 mr-2" />
                  Add Vector Mask
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
        {isGroup && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase text-muted-foreground">
              Group
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={onUngroup} className="text-xs">
              <FolderMinus className="h-3.5 w-3.5 mr-2" />
              Ungroup
            </DropdownMenuItem>
          </>
        )}
        {!isGroup && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase text-muted-foreground">
              Transform
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={onTransform}
              disabled={isMultiSelected}
              title={
                isMultiSelected
                  ? 'Free Transform on multiple layers is not supported in v1 — Merge Layers first, then transform.'
                  : undefined
              }
              className="text-xs"
            >
              <Move3D className="h-3.5 w-3.5 mr-2" />
              Free Transform…
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onLiquify}
              disabled={isMultiSelected}
              title={isMultiSelected ? MULTI_SELECT_DISABLED_TITLE : undefined}
              className="text-xs"
            >
              <Waves className="h-3.5 w-3.5 mr-2" />
              Liquify…
            </DropdownMenuItem>
            {canTrim && (
              <DropdownMenuItem onClick={onTrim} className="text-xs">
                <Scissors className="h-3.5 w-3.5 mr-2" />
                Trim Transparent Pixels
              </DropdownMenuItem>
            )}
            {canMerge && (
              <DropdownMenuItem onClick={onMerge} className="text-xs">
                <Merge className="h-3.5 w-3.5 mr-2" />
                Merge Layers
              </DropdownMenuItem>
            )}
            {isVector && (
              <DropdownMenuItem
                onClick={onEditPath}
                disabled={isMultiSelected}
                title={isMultiSelected ? MULTI_SELECT_DISABLED_TITLE : undefined}
                className="text-xs"
              >
                <Spline className="h-3.5 w-3.5 mr-2" />
                Edit Path…
              </DropdownMenuItem>
            )}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onDelete}
          className="text-xs text-destructive focus:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5 mr-2" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
